import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { MedicalBackgroundQuestionnaireItem } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/questionnaire'
import { TherapiesFormField } from './formField'

/**
 * Therapies subsection adapter
 * Handles therapy services and home exercise plans (5 fields)
 */
export class TherapiesAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Direct field extractions
    const therapies = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPIES
    )

    const therapyProvider = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPY_PROVIDER
    )?.at(0)

    const homeExercisePlan = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_PLAN
    )?.at(0)

    const homeExerciseDuration = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_DURATION
    )?.at(0)

    const homeExerciseFrequency = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_FREQUENCY
    )?.at(0)

    return {
      [TherapiesFormField.THERAPIES_THERAPIES]: therapies,
      [TherapiesFormField.THERAPIES_THERAPY_PROVIDER]: therapyProvider,
      [TherapiesFormField.THERAPIES_HOME_EXERCISE_PLAN]: homeExercisePlan,
      [TherapiesFormField.THERAPIES_HOME_EXERCISE_DURATION]: homeExerciseDuration,
      [TherapiesFormField.THERAPIES_HOME_EXERCISE_FREQUENCY]: homeExerciseFrequency
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPIES,
        value: this.getFormDataField(TherapiesFormField.THERAPIES_THERAPIES)
      }
    ]
    if (this.formDataFieldExists(TherapiesFormField.THERAPIES_THERAPY_PROVIDER)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPY_PROVIDER,
        value: this.getFormDataField(TherapiesFormField.THERAPIES_THERAPY_PROVIDER)
      })
    }
    if (this.formDataFieldExists(TherapiesFormField.THERAPIES_HOME_EXERCISE_PLAN)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_PLAN,
        value: this.getFormDataField(TherapiesFormField.THERAPIES_HOME_EXERCISE_PLAN)
      })
    }

    if (this.formDataFieldExists(TherapiesFormField.THERAPIES_HOME_EXERCISE_DURATION)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_DURATION,
        value: this.getFormDataField(TherapiesFormField.THERAPIES_HOME_EXERCISE_DURATION)
      })
    }
    if (this.formDataFieldExists(TherapiesFormField.THERAPIES_HOME_EXERCISE_FREQUENCY)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_FREQUENCY,
        value: this.getFormDataField(TherapiesFormField.THERAPIES_HOME_EXERCISE_FREQUENCY)
      })
    }

    return mutations
  }
}
